<template>
  <div class="autocomplete control" :class="{'is-expanded': expanded}">
    <d-input ref="input"
             v-model="searchValue"
             :disabled="disabled"
             type="text"
             :size="size"
             :loading="loading"
             :rounded="rounded"
             :icon="icon"
             :icon-pack="iconPack"
             :maxlength="maxlength"
             :autocomplete="newAutocomplete"
             v-bind="boundAttrs"
             @focus="focused"
             @blur="onBlur"
             @keyup.esc.prevent="escapePressed"
             @keydown.tab="tabPressed"
             @keydown.enter="enterPressed"
             @keydown.up.prevent="keyArrows('up')"
             @keydown.down.prevent="keyArrows('down')"
             @keyup="handleKeyUp"
    />

    <transition name="fade">
      <div v-show="isActive && (filteredItems.length > 0 || !hideEmpty) && (data.length > 0 || hasEmptySlot || hasHeaderSlot || !disabled)" ref="dropdown" class="dropdown-menu" :class="[newPosition]">
        <div v-show="isActive && !loading" class="dropdown-content">
          <div v-if="hasHeaderSlot" class="dropdown-item">
            <slot name="header" />
          </div>
          <a v-for="(option, index) in filteredItems" :key="index" class="dropdown-item" :class="{ 'is-hovered': option[itemKey] === hovered }" @click.stop.prevent="setSelected(option)">
            <slot v-if="hasDefaultSlot" :option="option" :index="index" />
            <span v-else>
              {{ getValue(option) }}
            </span>
          </a>
          <div v-if="filteredItems.length === 0 && hasEmptySlot && !loading" class="dropdown-item is-disabled">
            <slot name="empty" />
          </div>
          <div
            v-if="hasFooterSlot"
            class="dropdown-item"
          >
            <slot name="footer" />
          </div>
        </div>
        <div v-if="loading" class="dropdown-content">
          <slot v-if="hasLoadingSlot" name="loading" />
          <div class="flex justify-center">
            <d-loader :size="40" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import { defineComponent, nextTick } from 'vue';
  import FormElementMixin from '../../../utils/mixins/formElementMixin.js';
  import DInput from '../DInput/DInput.vue';
  import { getValueByPath } from '../../../utils/helpers.js';
  import { attribsWithoutClass } from '../../../utils/vue-helpers';

  export default defineComponent({
    name: 'DAutocomplete',
    components: {
      DInput,
    },
    mixins: [FormElementMixin],
    inheritAttrs: false,
    emits: ['focus', 'blur', 'typing', 'select', 'update:modelValue'],
    props: {
      disabled: Boolean,
      hideEmpty: Boolean,
      modelValue: [Number, String],
      data: {
        type: Array,
        default: () => [],
      },

      field: {
        type: String,
        default: 'value',
      },

      itemKey: {
        type: String,
        default: 'id',
      },

      filter: {
        type: Function,
        default: (item, queryText, itemText) => itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1,
      },

      /**
       * Set the default position when opened - `top`, `bottom`
       */
      position: {
        type: String,
        default: null,
      },
      keepFirst: Boolean,
      clearOnSelect: Boolean,
      openOnFocus: Boolean,
      customFormatter: Function,
    },
    data() {
      return {
        allItems: [],
        selected: null,
        hovered: null,
        isActive: false,
        searchValue: '',
        newAutocomplete: this.autocomplete || 'off',
        isListInViewportVertically: true,
        hasFocus: false,
        _isAutocomplete: true,
        _elementRef: 'input',
      };
    },
    computed: {
      boundAttrs() {
        return attribsWithoutClass(this.$attrs);
      },
      computedValue: {
        get() {
          return this.modelValue;
        },
        set(value) {
          this.$emit('update:modelValue', value);
        },
      },
      /**
       * White-listed items to not close when clicked.
       * Add input, dropdown and all children.
       */
      whiteList() {
        const whiteList = [];
        whiteList.push(this.$refs.input.$el.querySelector('input'));
        whiteList.push(this.$refs.dropdown);
        // Add all chidren from dropdown
        if (this.$refs.dropdown !== undefined) {
          const children = this.$refs.dropdown.querySelectorAll('*');
          for (const child of children) {
            whiteList.push(child);
          }
        }
        return whiteList;
      },

      filteredItems() {
        if (this.searchValue == null) return this.allItems;

        return this.allItems.filter((item) => this.filter(item, String(this.searchValue), String(item[this.field])));
      },

      /**
       * Check if exists default slot
       */
      hasDefaultSlot() {
        return !!this.$slots.default;
      },
      /**
       * Check if exists "empty" slot
       */
      hasEmptySlot() {
        return !!this.$slots.empty && !this.hideEmpty;
      },
      /**
       * Check if exists "footer" slot
       */
      hasFooterSlot() {
        return !!this.$slots.footer;
      },
      /**
       * Check if exists "header" slot
       */
      hasHeaderSlot() {
        return !!this.$slots.header;
      },
      /**
       * Check if exists "loading" slot
       */
      hasLoadingSlot() {
        return !!this.$slots.loading;
      },

      newPosition() {
        if (this.position) {
          return `is-opened-${this.position}`;
        }
        return this.isListInViewportVertically ? 'is-opened-bottom' : '';
      },
    },
    watch: {
      /**
       * When dropdown is toggled, check the visibility to know when
       * to open upwards.
       */
      isActive(active) {
        if (active) {
          this.calcDropdownInViewportVertical();
        } else {
          this.$nextTick(() => this.setHovered(null));
          // Timeout to wait for the animation to finish before recalculating
          setTimeout(() => {
            this.calcDropdownInViewportVertical();
          }, 100);
        }
      },
      /**
       * When updating input's value
       *   1. Emit changes
       *   2. If value isn't the same as selected, set null
       *   3. Close dropdown if value is clear or else open it
       */
      searchValue(value) {
        // Check if selected is invalid

        const currentValue = this.getValue(this.selected);
        if (currentValue && currentValue !== value) {
          this.setSelected(null, false);
        }
        // Close dropdown if input is clear or else open it
        if (this.hasFocus && (!this.openOnFocus || value)) {
          this.isActive = !!value;
        }
      },
      /**
       * When v-model is changed Update internal value.
       */
      modelValue(value) {
        this.updateDisplayValue();
      },
      /**
       * Select first option if "keep-first
       */
      data(value) {
        // Keep first option always pre-selected
        if (this.keepFirst) {
          this.selectFirstOption(value);
        }
        this.allItems = JSON.parse(JSON.stringify(value));
        this.updateDisplayValue();
      },
    },
    created() {
      if (typeof window !== 'undefined') {
        document.addEventListener('click', this.clickedOutside);
        window.addEventListener('resize', this.calcDropdownInViewportVertical);
      }
    },
    beforeUnmount() {
      if (typeof window !== 'undefined') {
        document.removeEventListener('click', this.clickedOutside);
        window.removeEventListener('resize', this.calcDropdownInViewportVertical);
      }
    },
    methods: {
      escapePressed(ev) {
        ev.stopPropagation();
        this.isActive = false;
        return false;
      },
      /**
       * Set which option is currently hovered.
       */
      setHovered(option) {
        if (!option) {
          this.hovered = null;
          return;
        }
        this.hovered = option[this.itemKey];
      },

      updateDisplayValue(value = this.modelValue) {
        const copy = { ...value };
        if (typeof this.value === 'object') copy = this.getValue(copy);
        const item = this.allItems.find((m) => m[this.field] === copy || m[this.itemKey] === copy);
        if (item) {
          this.searchValue = item[this.field];
          this.computedValue = item[this.itemKey];
        }
      },

      /**
       * Set which option is currently selected, update v-model,
       * update input value and close dropdown.
       */
      setSelected(option, closeDropdown = true) {
        if (option === undefined) return;
        this.selected = option;
        this.$emit('select', this.selected);
        if (this.selected !== null) {
          this.computedValue = this.selected[this.itemKey];
          this.searchValue = this.clearOnSelect ? '' : this.selected[this.field];
        }
        closeDropdown && this.$nextTick(() => { this.isActive = false; });
      },
      /**
       * Select first option
       */
      selectFirstOption(options) {
        this.$nextTick(() => {
          if (options.length) {
            // If has visible data or open on focus, keep updating the hovered
            if (this.openOnFocus || (this.searchValue !== '' && this.hovered !== options[0])) {
              this.setHovered(options[0]);
            }
          } else {
            this.setHovered(null);
          }
        });
      },
      /**
       * Enter key listener.
       * Select the hovered option.
       */
      enterPressed(ev) {
        if (this.hovered !== null) {
          this.setSelected(this.data.find((d) => d[this.itemKey] === this.hovered));
          ev.preventDefault();
        } else {
          this.isActive = false;
        }
      },
      /**
       * Tab key listener.
       * Select hovered option if it exists, close dropdown, then allow
       * native handling to move to next tabbable element.
       */
      tabPressed() {
        if (this.hovered === null) {
          this.isActive = false;
          return;
        }
        this.setSelected(this.hovered);
      },
      /**
       * Close dropdown if clicked outside.
       */
      clickedOutside(event) {
        if (this.whiteList.indexOf(event.target) < 0) this.isActive = false;
      },
      /**
       * Return display text for the input.
       * If object, get value from path, or else just the value.
       */
      getValue(option) {
        if (!option) return;
        if (typeof this.customFormatter !== 'undefined') {
          return this.customFormatter(option);
        }
        return typeof option === 'object'
          ? getValueByPath(option, this.field)
          : option;
      },
      /**
       * Calculate if the dropdown is vertically visible when activated,
       * otherwise it is openened upwards.
       */
      calcDropdownInViewportVertical() {
        this.$nextTick(() => {
          /**
           * this.$refs.dropdown may be undefined
           * when Autocomplete is conditional rendered
           */
          if (this.$refs.dropdown === undefined) return;
          const rect = this.$refs.dropdown.getBoundingClientRect();
          this.isListInViewportVertically = (
            rect.top >= 0
            && rect.bottom <= (window.innerHeight
              || document.documentElement.clientHeight)
          );
        });
      },
      /**
       * Arrows keys listener.
       * If dropdown is active, set hovered option, or else just open.
       */
      keyArrows(direction) {
        const sum = direction === 'down' ? 1 : -1;
        if (this.isActive) {
          const collection = this.filteredItems;
          let index = collection.map((d) => d[this.itemKey]).indexOf(this.hovered) + sum;
          index = index > collection.length - 1 ? collection.length : index;
          index = index < 0 ? 0 : index;

          this.setHovered(collection[index]);
          const list = this.$refs.dropdown.querySelector('.dropdown-content');
          const element = list.querySelectorAll('a.dropdown-item:not(.is-disabled)')[index];
          if (!element) return;
          const visMin = list.scrollTop;
          const visMax = list.scrollTop + list.clientHeight - element.clientHeight;
          if (element.offsetTop < visMin) {
            list.scrollTop = element.offsetTop;
          } else if (element.offsetTop >= visMax) {
            list.scrollTop = (
              element.offsetTop
              - list.clientHeight
              + element.clientHeight
            );
          }
        } else {
          this.isActive = true;
        }
      },
      /**
       * Focus listener.
       * If value is the same as selected, select all text.
       */
      focused(event) {
        if (this.getValue(this.selected) === this.searchValue) {
          this.$el.querySelector('input').select();
        }
        if (this.openOnFocus) {
          this.isActive = true;
          if (this.keepFirst) {
            this.selectFirstOption(this.data);
          }
        }
        this.hasFocus = true;
        this.$emit('focus', event);
      },
      /**
       * Blur listener.
       */
      onBlur(event) {
        this.hasFocus = false;
        this.$emit('blur', event);
      },
      handleKeyUp() {
        this.$emit('typing', this.searchValue);
      },
    },
  });
</script>